import { FC, useMemo, useState } from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import { CaretDownIcon, FormCloseIcon, GreyStrokeInfoIcon, RedirectIcon } from 'assets/icons';
import Button, { ButtonType } from 'components/custom-button/CustomButton';
import { VoidFn } from 'types/baseTypes';
import useFSMPlanInfo from '../hooks/useFSMPlanInfo';
import { openInNewTab } from 'utils/commonUtils';

type UpgradeFromAppInfoModalProps = {
  handleClose: VoidFn;
};

const UpgradeFromAppInfoModal: FC<UpgradeFromAppInfoModalProps> = ({ handleClose }) => {
  const { storeRedirectUrl, source } = useFSMPlanInfo();
  const [showExplanation, setShowExplanation] = useState(false);

  const platformName = useMemo(() => {
    if (source === 'ios') {
      return 'App Store';
    }
    if (source === 'android') {
      return 'Google Play';
    }
    return 'store';
  }, [source]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClose();
    }
  };

  const handleToggleExplanation = () => {
    setShowExplanation(prev => !prev);
  };

  const handleRedirectToSubscription = () => {
    if (storeRedirectUrl) {
      openInNewTab(storeRedirectUrl);
    }
    handleClose();
  };

  return (
    <CustomModal open={true} closeModal={handleClose} width="sm:w-[90%] md:w-[590px]" height="lg:max-h-[95vh]">
      <div className="relative w-full rounded-xl bg-white p-6">
        <FormCloseIcon
          onClick={handleClose}
          className="absolute right-3 top-3 h-6 w-6 cursor-pointer"
          tabIndex={0}
          aria-label="Close modal"
          onKeyDown={handleKeyDown}
        />

        {/* Title */}
        <h2 className="mb-6 text-center text-2xl font-bold text-heading">Cancel your current plan first</h2>

        {/* Introductory text */}
        <p className="mb-4 text-17px text-primaryText">
          You're currently subscribed to Field Serve Lite from your mobile app.
        </p>

        {/* Next steps */}
        <div className="mb-4">
          <h3 className="mb-2.5 text-17px font-semibold text-primaryText">To upgrade:</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full
             bg-secondaryBtn text-sm font-semibold text-tertiaryText">
                1
              </div>
              <div>
                <p className="mb-1 text-sbase text-primaryText">
                  <b>Cancel your Field Serve Lite subscription</b> from your {platformName} settings (mobile or web)
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full 
             bg-secondaryBtn text-sm font-semibold text-tertiaryText">
                2
              </div>
              <div>
                <p className="mb-1 text-sbase text-primaryText">
                  <b>Return to this page</b> to subscribe to Field Serve Plus
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Info notice */}
        <div className="mb-5 flex items-center gap-1.5 border-t border-borderGray pt-3">
          <GreyStrokeInfoIcon className="path-fill-current mt-0.5 h-3.5 w-3.5 shrink-0 text-accentText" />
          <p className="text-sbase text-accentText">
            Your data and jobs remain available. You can upgrade anytime after cancellation.
          </p>
        </div>

        {storeRedirectUrl && (
          <Button
            childrenContainerStyles="text-17px"
            EndIcon={RedirectIcon}
            endIconStyle="path-fill-current text-primary h-5 w-5 shrink-0"
            onClick={handleRedirectToSubscription}
            type={ButtonType.ALERT}
            className="mb-4 h-12 w-full"
            id="redirect-subscription-btn"
            aria-label="Cancel Field Serve Lite, opens in new tab">
            Cancel Field Serve Lite
          </Button>
        )}
        <Button
          childrenContainerStyles="text-17px"
          onClick={handleClose}
          type={ButtonType.SECONDARY}
          className="h-12 w-full border-primaryBtn text-primaryBtn hover:bg-primaryBtn/5"
          id="close-info-modal-btn"
          aria-label="Not now, close modal">
          I'll do this later
        </Button>
        <div className="mt-5 border-t border-borderGray pt-5">
          <div
            onClick={handleToggleExplanation}
            role="button"
            tabIndex={0}
            aria-expanded={showExplanation}
            aria-label="Toggle explanation about why ScanPay cannot cancel subscription"
            className="flex cursor-pointer items-center justify-center gap-2 
            text-17px font-semibold text-primaryBtn">
            <span>Why can't ScanPay cancel it for me?</span>
            <CaretDownIcon
              className={`path-stroke-current shrink-0 text-primaryBtn
              ${showExplanation ? 'rotate-180' : ''}`}
            />
          </div>
          {showExplanation && (
            <p className="mt-2 text-sbase text-primaryText">
              <span className="capitalize">{platformName}</span> policies require users to manage their own
              subscriptions. This protects your billing information and ensures you control all payments.
            </p>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default UpgradeFromAppInfoModal;
